import {
  createContext,
  use,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import type { AppSupabaseClient } from '../data/supabaseClient';
import type { Repositories } from '../data/repositories/contracts';
import type { GrowerProfile } from '../domain/models/records';

type StudioContextValue = {
  repositories: Repositories;
  profile: GrowerProfile | undefined;
  ready: boolean;
  error: string | undefined;
  /** Invalidates cached repository reads after a successful mutation. */
  refresh: () => void;
};

const StudioContext = createContext<StudioContextValue | undefined>(undefined);

export function StudioProvider({
  children,
  repositories: injectedRepositories,
  client: injectedClient,
}: {
  children: ReactNode;
  /** Test override; defaults to the real Supabase-backed repositories. */
  repositories?: Repositories;
  /** Test override; defaults to the real Supabase client. */
  client?: AppSupabaseClient;
}): ReactNode {
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: { retry: false, refetchOnWindowFocus: false },
    },
  }));

  return (
    <QueryClientProvider client={queryClient}>
      <StudioContextProvider
        {...(injectedRepositories === undefined
          ? {}
          : { repositories: injectedRepositories })}
        {...(injectedClient === undefined ? {} : { client: injectedClient })}
      >
        {children}
      </StudioContextProvider>
    </QueryClientProvider>
  );
}

function StudioContextProvider({
  children,
  repositories: injectedRepositories,
  client: injectedClient,
}: {
  children: ReactNode;
  repositories?: Repositories;
  client?: AppSupabaseClient;
}): ReactNode {
  const queryClient = useQueryClient();
  const client = useMemo(() => injectedClient ?? supabase, [injectedClient]);

  const [repositories, setRepositories] = useState<Repositories | undefined>(
    injectedRepositories,
  );

  const [profile, setProfile] = useState<GrowerProfile | undefined>();
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (injectedRepositories) {
      return;
    }

    let cancelled = false;
    void import('../data/repositories/supabaseRepositories')
      .then(({ createSupabaseRepositories }) => {
        if (!cancelled) setRepositories(createSupabaseRepositories(client));
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setError(
            cause instanceof Error
              ? cause.message
              : 'The data module could not be loaded.',
          );
          setReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [client, injectedRepositories]);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async (): Promise<void> => {
      if (!repositories) return;
      try {
        // Seed reference data lives in the database (migrations); the client
        // only ensures the account has a grower profile row.
        const ensured = await repositories.profile.ensure();

        if (!cancelled) {
          setProfile(ensured);
          setReady(true);
          setError(undefined);
        }
      } catch (cause) {
        if (!cancelled) {
          setError(
            cause instanceof Error
              ? cause.message
              : 'Your account data could not be loaded.',
          );
          setReady(true);
        }
      }
    };

    void bootstrap();

    return () => {
      cancelled = true;
    };
  }, [repositories]);

  const value = useMemo<StudioContextValue | undefined>(
    () => repositories ? ({
      repositories,
      profile,
      ready,
      error,
      refresh: () => {
        void queryClient.invalidateQueries({ queryKey: ['repository'] });
      },
    }) : undefined,
    [repositories, profile, ready, error, queryClient],
  );

  if (!value) {
    return error
      ? <p role="alert">GrowHub could not load the data module: {error}</p>
      : <p role="status">Loading your GrowHub data…</p>;
  }

  return <StudioContext value={value}>{children}</StudioContext>;
}

export function useStudio(): StudioContextValue {
  const value = use(StudioContext);

  if (!value) {
    throw new Error('useStudio must be used inside a StudioProvider.');
  }

  return value;
}

export function useRepositories(): Repositories {
  return useStudio().repositories;
}
